/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { finalize, filter, map, startWith, switchMap, take } from 'rxjs/operators';

import { StripeCardNumberElement } from '@stripe/stripe-js';
import { CreditCardStore } from '@sondermind/data-access/client-credit-card';
import { STRIPE_LOADER, StripePaymentMethodBase } from '@sondermind/features/stripe-payment-method';
import { PaymentMethodStore } from '@sondermind/data-access/payment-method';
import { NotificationToastService, ToastType } from '@sondermind/features/notification-toast';
import { ConfigurationService, IClientAppConfig } from '@sondermind/configuration';
import { AccountSetupService } from '@sondermind/utilities/services';
import { Logger } from '@sondermind/datadog-logging';
import { IrisFormFieldModule } from '@sondermind/iris/form-field';
import { IrisTextInputModule } from '@sondermind/iris/text-input';
import { IrisButtonModule, IrisButtonTypes } from '@sondermind/iris/button';
import { IrisCheckboxModule } from '@sondermind/iris/checkbox';
import { IrisBannerComponent, IrisBannerType } from '@sondermind/iris/banner';
import { IrisSpinnerComponent } from '@sondermind/iris/spinner';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';
import { ClientInsuranceHttpService, PatientInsuranceStore } from '@sondermind/data-access/client-insurance';
import { ApiError } from '@sondermind/utilities/models-http';
import { InfoLinks } from '@sondermind/core/enums/info-links.enum';

@Component({
  selector: 'client-credit-card-add',
  templateUrl: './credit-card-add.component.html',
  styleUrls: ['./credit-card-add.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IrisFormFieldModule,
    IrisTextInputModule,
    IrisButtonModule,
    IrisCheckboxModule,
    IrisBannerComponent,
    IrisSpinnerComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CreditCardAddComponent implements OnInit, OnDestroy {
  // used for embedding form in new client account setup flow
  readonly useInline = input(false);
  readonly useInSessionRequest = input(false);
  readonly closeAdd = output<void>();
  readonly showFinancialAgreement = input(false);

  // Dependency injection
  private readonly logger = inject(Logger);
  private readonly config = inject(ConfigurationService<IClientAppConfig>);
  private readonly paymentMethodStore = inject(PaymentMethodStore);
  private readonly creditCardStore = inject(CreditCardStore);
  private readonly toastService = inject(NotificationToastService);
  private readonly accountSetupService = inject(AccountSetupService);
  private readonly patientInsuranceStore = inject(PatientInsuranceStore);
  private readonly clientInsuranceHttp = inject(ClientInsuranceHttpService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);

  // Stripe instance (composition, not inheritance)
  private readonly stripeBase: StripePaymentMethodBase;

  // Delegate Stripe state to StripePaymentMethodBase signals
  readonly loaded: ReturnType<typeof signal<boolean>>;
  readonly loadError: ReturnType<typeof signal<unknown | null>>;
  readonly stripeNotLoaded: ReturnType<typeof computed<boolean>>;
  readonly cardErrorMessage: ReturnType<typeof signal<{ title: string; } | null>>;
  readonly stripeFormComplete: ReturnType<typeof computed<boolean>>;

  // ViewChild for Stripe form element
  private readonly creditCardForm = viewChild<ElementRef<HTMLFormElement>>('creditCardForm');

  constructor() {
    // Initialize Stripe after config is available
    this.stripeBase = new StripePaymentMethodBase(
      this.config.env.app.stripeKey,
      this.logger,
      inject(STRIPE_LOADER)
    );

    // Assign delegated signals after stripeBase is initialized
    this.loaded = this.stripeBase.loaded;
    this.loadError = this.stripeBase.loadError;
    this.stripeNotLoaded = this.stripeBase.stripeNotLoaded;
    this.cardErrorMessage = this.stripeBase.cardErrorMessage;
    this.stripeFormComplete = this.stripeBase.stripeFormComplete;

    // Register effect in constructor (injection context)
    effect(() => {
      const isLoaded = this.loaded();
      const formRef = this.creditCardForm();
      if (isLoaded && formRef) {
        const elements = this.stripeBase.mountStripeElements();
        this.stripeBase.registerStripeElements(elements, formRef.nativeElement);
      }
    });
  }

  // Component state
  readonly submitting = signal(false);

  // Forms (typed)
  readonly ccForm: FormGroup<{
    nickname: FormControl<string | null>;
  }> = this.fb.group({
      nickname: ['']
    });

  readonly financialPolicyForm: FormGroup<{
    acceptedFinancialPolicy: FormControl<boolean | null>;
  }> = this.fb.group({
      acceptedFinancialPolicy: [false]
    });

  // Convert form status to signals for reactive validation
  private readonly financialPolicyAccepted = toSignal(this.financialPolicyForm.valueChanges.pipe(
    startWith(this.financialPolicyForm.value.acceptedFinancialPolicy ?? false),
    map(() => this.financialPolicyForm.value.acceptedFinancialPolicy ?? false)
  ), { initialValue: false });

  // Computed form validity
  readonly formValid = computed(() => {
    const isStripeValid = this.stripeFormComplete();

    if (this.showFinancialAgreement()) {
      const policyAccepted = this.financialPolicyAccepted();
      return isStripeValid && policyAccepted;
    }

    return isStripeValid;
  });

  // Expose enums for template
  readonly IrisButtonTypes = IrisButtonTypes;
  readonly IrisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;
  readonly irisBannerType = IrisBannerType;

  financialResponsibilityAgreement = InfoLinks.FINANCIAL_RESPONSIBILITY_AGREEMENT;

  ngOnInit(): void {
    // Subscribe to form submit events
    this.stripeBase.stripeElementSubject$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.saveNewCard();
      });
  }

  ngOnDestroy(): void {
    // Cleanup is automatic with DestroyRef and takeUntilDestroyed
    this.stripeBase.cleanupEventListeners();
  }

  saveNewCard(): void {
    this.submitting.set(true);

    const nickname = this.ccForm.value.nickname || '';

    // Use PaymentMethodStore with SetupIntent flow
    this.paymentMethodStore.createPaymentMethod(
      ['card'], // payment method types
      nickname,
      this.accountSetupService.context,
      (clientSecret: string) => this.stripeBase.confirmPaymentMethodSetup(
        this.stripeBase.stripeFormElements[0] as StripeCardNumberElement, // card number element
        clientSecret
      ).pipe(
        map((result) => ({
          paymentMethodId: result.paymentMethodId,
          type: result.type
        }))
      )
    ).pipe(
      finalize(() => this.submitting.set(false)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (paymentMethod) => {
        // Guard against null emission from known failure paths:
        // missing client_secret, missing paymentMethodId, malformed response, or 404
        if (!paymentMethod) {
          this.toastService.showNotificationToast({
            message: 'Card could not be saved — please try again.',
            type: ToastType.ERROR
          });
          this.ccForm.setErrors({ saveError: true });
          return;
        }
        this.handleSuccess();
      },
      error: (err) => {
        this.handleError(err);
      }
    });
  }

  private handleSuccess(): void {
    // Add cash pay insurance if user doesn't have it
    this.patientInsuranceStore.hasCashPay$.pipe(
      take(1),
      filter((hasCashPay) => !hasCashPay),
      switchMap(() => this.clientInsuranceHttp.addCashPay())
    ).subscribe({
      next: () => {
        this.patientInsuranceStore.loadPatientInsuranceList();
      },
      error: (err) => {
        this.logger.warn('Failed to add cash pay insurance after credit card save', { err });
      }
    });

    // Show success message (unless embedded inline)
    if (!this.useInline()) {
      this.toastService.showNotificationToast({
        message: 'Credit card saved',
        type: ToastType.SUCCESS
      });
    }

    // Refresh credit card list to show new card
    this.creditCardStore.loadCreditCardList();

    // Emit close event to parent
    this.closeAdd.emit();
  }

  private handleError(err: ApiError | Error): void {
    // Extract error details if this is an ApiError
    const apiError = err instanceof ApiError ? err : null;
    const status = apiError?.status;
    const base = apiError?.base?.[0];
    const fieldErrors = apiError?.fields;
    const firstFieldError = fieldErrors
      ? Object.values(fieldErrors)[0] as string[] | undefined
      : undefined;

    // Branch on HTTP status code to distinguish validation errors from server errors
    if (status === 422) {
      // Validation error - try to show specific message
      const validationMessage =
        base ??
        firstFieldError?.[0] ??
        'Your payment method could not be validated. Please double check your payment details below.';

      this.toastService.showNotificationToast({
        message: validationMessage,
        type: ToastType.ERROR
      });
    } else {
      // Server error or other error
      const serverMessage =
        base ?? 'There was an error processing your payment method. Please try again later.';

      this.toastService.showNotificationToast({
        message: serverMessage,
        type: ToastType.ERROR
      });
    }

    // Set form-level error for UI feedback
    this.ccForm.setErrors({ saveError: true });
  }
}
